import {
  CheckboxYesNo,
  FormContainer,
  LabeledInput,
  stateOptions,
  Worksheet
} from "uikit"
import { AddRealEstateModal } from "components/accounts/AddRealEstateModal"
import React from "react"
import { Dropdown, Grid } from "semantic-ui-react"
import { useStore } from "store"
import { Footer } from "./Footer"

const gridHeaderStyle = {
  borderBottom: "2px solid #dedede",
  padding: "1em 0em 1em 1.2em",
  fontSize: "1.5em",
  fontWeight: "700"
}

const HouseholdProfileForm = ({
  data: {
    fields: {
      primary_state_of_residence,
      is_renter,
      monthly_rent,
      primary_residence
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onCancel,
  baseUrl,
  primaryResidenceOptions,
  addPropertyModalSubmit,
  isPrimaryResidenceDefaultValue
}) => {
  const addModalOptions = {
    values: { is_primary_residence: isPrimaryResidenceDefaultValue }
  }
  return (
    <Worksheet error={error}>
      <Grid.Row className="white" style={gridHeaderStyle}>
        <Grid.Column>Edit Residency</Grid.Column>
      </Grid.Row>
      <Worksheet.Body>
        <Worksheet.Row label="Primary state of residence" paddedField>
          <Worksheet.Field error={primary_state_of_residence.error}>
            <Dropdown
              {...primary_state_of_residence}
              fluid
              search
              selection
              options={stateOptions}
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row label="Do you own your primary residence?" paddedField>
          <Worksheet.Field>
            <CheckboxYesNo
              fluid
              name={is_renter.name}
              checked={
                is_renter.checked === undefined
                  ? is_renter.checked
                  : !is_renter.checked
              }
              onChange={(e, { value }) => {
                is_renter.onChange(e, { value: !value })
                monthly_rent.onChange(e, { value: value ? "" : 0 })
              }}
            />
          </Worksheet.Field>
          {is_renter.checked ? (
            <Worksheet.Row
              label="What is your monthly rent amount?"
              paddedField
            >
              <Worksheet.Field error={monthly_rent.error}>
                <LabeledInput
                  {...monthly_rent}
                  label=""
                  iconText="$"
                  iconPosition="left"
                  placeholder="0"
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : is_renter.checked !== undefined ? (
            <Worksheet.Row
              label="What property is your primary residence?"
              paddedField
            >
              <Worksheet.Field>
                <Dropdown
                  {...primary_residence}
                  placeholder="Primary residence"
                  options={[
                    ...primaryResidenceOptions,
                    { text: "Add Real Estate...", value: "__ADD__" }
                  ]}
                  className="has-add-option"
                  search
                  selection
                />
                {primary_residence.value === "__ADD__" && (
                  <AddRealEstateModal
                    submit={propertyValues =>
                      addPropertyModalSubmit(propertyValues).then(
                        ({ propertyId }) =>
                          primary_residence.onChange(null, {
                            value: propertyId
                          })
                      )
                    }
                    onCancel={() =>
                      primary_residence.onChange(null, { value: null })
                    }
                    {...addModalOptions}
                  />
                )}
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
        <Footer
          baseUrl={baseUrl}
          submitLabel="Done"
          isNew={true}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isPending={isPending}
          isModified={isModified}
          deleteLabel="Cancel"
          error={error}
        />
      </Worksheet.Body>
    </Worksheet>
  )
}

const HouseholdProfileWorksheetFormContainer = FormContainer(
  {
    primary_state_of_residence: {
      type: "string",
      required: true
    },
    is_renter: {
      type: "boolean",
      required: false
    },
    monthly_rent: {
      type: "unit",
      defaultValue: 100,
      minValue: 0,
      required: false
    },
    primary_residence: {
      type: "string",
      required: false
    }
  },
  ({ fields: { is_renter, monthly_rent } }, ...form) => {
    if (is_renter.checked && monthly_rent.value === "") {
      return {
        fields: {
          monthly_rent: "This field is required."
        }
      }
    }
  }
)(HouseholdProfileForm)

const HouseholdProfileWorksheet = ({ household, ...formProps }) => {
  const [
    properties,
    { updateHousehold, updateProperty, addProperty }
  ] = useStore("householdProperties", household.id)

  const primaryResidenceOptions = Object.values(properties)
    .filter(property => property.type === "realestate")
    .map(property => ({
      text: property.description,
      value: property.id
    }))
  const primaryResidence = Object.values(properties).find(
    property => property.is_primary_residence
  )
  const isPrimaryResidenceDefaultValue = !primaryResidence
  const values = {
    primary_state_of_residence: household.primary_state_of_residence,
    is_renter: household.is_renter,
    monthly_rent: household.monthly_rent,
    primary_residence: primaryResidence ? primaryResidence.id : ""
  }

  const submit = async values => {
    const {
      is_renter,
      monthly_rent,
      primary_state_of_residence,
      primary_residence
    } = values
    await updateHousehold({
      id: household.id,
      primary_state_of_residence,
      is_renter,
      monthly_rent: is_renter ? monthly_rent || 0 : null
    })
    if (is_renter === false && primary_residence) {
      const currentPrimaryResidence = Object.values(properties).find(
        property => property.is_primary_residence === true
      )
      if (currentPrimaryResidence) {
        await updateProperty({
          id: currentPrimaryResidence.id,
          is_primary_residence: false,
          type: "realestate"
        })
      }
      await updateProperty({
        id: primary_residence,
        is_primary_residence: true,
        type: "realestate"
      })
    }
  }
  const addPropertyModalSubmit = async values =>
    await addProperty({
      household_id: household.id,
      type: "realestate",
      ...values
    })
  const onDelete = () => true
  return (
    <HouseholdProfileWorksheetFormContainer
      {...{
        submit,
        onDelete,
        values,
        primaryResidenceOptions,
        addPropertyModalSubmit,
        isPrimaryResidenceDefaultValue,
        ...formProps
      }}
    />
  )
}

export default HouseholdProfileWorksheet
